import { createContext, useContext, type ReactNode } from "react";
import type { Map as MapboxMap } from "mapbox-gl";
import type { AssignmentArea } from "../api/types";

/** Optional `assignmentId` ties map highlight to list selection (admin faint vs strong polygons). */
export type SetHighlightedAssignmentAreaOptions = {
    assignmentId?: string | null;
};

interface MapContextValue {
    map: MapboxMap | null;
    isMapLoaded: boolean;
    isDrawingPolygon: boolean;
    polygonArea: AssignmentArea | null;
    /** Read-only highlight on admin map (e.g. show saved assignment area without edit mode). */
    highlightedAssignmentArea: AssignmentArea | null;
    /** When set with a highlighted area, the matching polygon is omitted from the faint admin overview. */
    highlightedAssignmentId: string | null;
    setHighlightedAssignmentArea: (area: AssignmentArea | null, options?: SetHighlightedAssignmentAreaOptions) => void;
    /** Bumps when showing an area so MapView re-applies layers (e.g. same polygon clicked again after style change). */
    assignmentHighlightNonce: number;
    startPolygonDrawing: (initialArea?: AssignmentArea, onComplete?: (area: AssignmentArea | null) => void) => void;
    stopPolygonDrawing: () => void;
    setPolygonArea: (area: AssignmentArea | null) => void;
}

const MapContext = createContext<MapContextValue | null>(null);

export const useMapContext = (): MapContextValue => {
    const context = useContext(MapContext);
    if (!context) {
        throw new Error("useMapContext must be used within MapContextProvider");
    }
    return context;
};

// Optional version for components that can work with or without map
export const useMapContextOptional = (): MapContextValue | null => {
    return useContext(MapContext);
};

interface MapContextProviderProps {
    children: ReactNode;
    map: MapboxMap | null;
    isMapLoaded: boolean;
    isDrawingPolygon: boolean;
    polygonArea: AssignmentArea | null;
    highlightedAssignmentArea: AssignmentArea | null;
    highlightedAssignmentId?: string | null;
    onHighlightedAssignmentAreaChange: (area: AssignmentArea | null, options?: SetHighlightedAssignmentAreaOptions) => void;
    assignmentHighlightNonce: number;
    onStartDrawing: (initialArea?: AssignmentArea, onComplete?: (area: AssignmentArea | null) => void) => void;
    onStopDrawing: () => void;
    onAreaChange: (area: AssignmentArea | null) => void;
}

export const MapContextProvider = ({
    children,
    map,
    isMapLoaded,
    isDrawingPolygon,
    polygonArea,
    highlightedAssignmentArea,
    highlightedAssignmentId = null,
    onHighlightedAssignmentAreaChange,
    assignmentHighlightNonce,
    onStartDrawing,
    onStopDrawing,
    onAreaChange
}: MapContextProviderProps): JSX.Element => {
    return (
        <MapContext.Provider
            value={{
                map,
                isMapLoaded,
                isDrawingPolygon,
                polygonArea,
                highlightedAssignmentArea,
                highlightedAssignmentId,
                setHighlightedAssignmentArea: onHighlightedAssignmentAreaChange,
                assignmentHighlightNonce,
                startPolygonDrawing: onStartDrawing,
                stopPolygonDrawing: onStopDrawing,
                setPolygonArea: onAreaChange
            }}
        >
            {children}
        </MapContext.Provider>
    );
};